'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Building2,
  Eye,
  Shield,
  Zap,
  Smartphone,
  BarChart3,
  Users,
  Wrench,
  SlidersHorizontal
} from 'lucide-react';
import { cn } from '@/lib/utils';

const Features = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Building2,
      title: 'Universal Platform',
      description: 'Manage any property type from one dashboard — no switching tools, no workarounds.',
      highlight: 'So you can grow without starting over.'
    },
    {
      icon: Eye,
      title: 'Real-Time Visibility',
      description: 'Live dashboards for you, your team, and your property owners. Everyone stays informed.',
      highlight: 'So you can answer questions before they\'re asked.'
    },
    {
      icon: Shield,
      title: 'Vendor Accountability',
      description: 'Track performance, ratings, certifications, and response times for every vendor.',
      highlight: 'So you can hold vendors to the standard.'
    },
    {
      icon: Zap,
      title: 'Smart Prioritization',
      description: 'Automatic issue detection that categorizes and prioritizes work orders for you.',
      highlight: 'So nothing critical slips through the cracks.'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Design',
      description: 'Submit issues, track progress, and manage operations from anywhere on any device.',
      highlight: 'So you\'re never tied to a desk.'
    },
    {
      icon: BarChart3,
      title: 'Professional Reporting',
      description: 'Automated reports, compliance documentation, and analytics that make you look good.',
      highlight: 'So you can show value, not just tell it.'
    },
    {
      icon: Users,
      title: 'Tenant Portal',
      description: 'Clean, simple issue reporting for occupants. No training required.',
      highlight: 'So tenants love reporting instead of complaining.'
    },
    {
      icon: Wrench,
      title: 'Preventive Maintenance',
      description: 'Schedule recurring tasks, track asset lifecycles, and prevent issues before they happen.',
      highlight: 'So you can be proactive, not reactive.'
    },
    {
      icon: SlidersHorizontal,
      title: 'Fully Configurable',
      description: 'Customize workflows, labels, categories, and terminology for each property type in your portfolio.',
      highlight: 'So it works the way you do — not the other way around.'
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-500/[0.02] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span
            className={cn(
              "inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase",
              "bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            Platform Features
          </span>
          <h2
            className={cn(
              "font-playfair text-4xl md:text-5xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            Enterprise Technology.
            <br />
            <span className="text-primary-600 dark:text-primary-400">Accessible Pricing.</span>
          </h2>
          <p
            className={cn(
              "max-w-2xl mx-auto text-lg text-gray-500 dark:text-secondary-400",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '200ms' }}
          >
            Built for property professionals who are tired of duct-taping tools together.
            Every feature earns its place.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => (
            <div
              key={i}
              className={cn(
                "group relative p-6 rounded-2xl",
                "bg-white shadow-sm dark:bg-white/[0.03] dark:shadow-none",
                "border border-gray-200 dark:border-white/[0.06]",
                "hover:border-primary-500/20 hover:bg-gray-50 dark:hover:bg-white/[0.04]",
                "transition-all duration-500",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: `${300 + i * 75}ms` }}
            >
              {/* Icon */}
              <div className={cn(
                "w-12 h-12 rounded-xl mb-4 flex items-center justify-center",
                "bg-primary-500/10 border border-primary-500/20",
                "group-hover:bg-primary-500/20 group-hover:border-primary-500/30",
                "transition-all duration-300"
              )}>
                <feature.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-secondary-100 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-secondary-400 mb-3 leading-relaxed">
                {feature.description}
              </p>
              <p className="text-xs text-primary-600/80 dark:text-primary-400/80 italic">
                {feature.highlight}
              </p>

              {/* Hover glow */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </div>
          ))}
        </div>

        {/* Bottom callout */}
        <div
          className={cn(
            "mt-16 text-center",
            "transition-all duration-700",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{ transitionDelay: '900ms' }}
        >
          <p className="text-gray-400 dark:text-secondary-500">
            And that's just the beginning.{' '}
            <span className="text-gray-600 dark:text-secondary-300">50+ features</span>
            {' '}designed for property professionals.
          </p>
        </div>
      </div>
    </section>
  );
};

export { Features };
